import { useState } from "react";
import type { SpatialNode } from "../engine/types";
import { useEngineStore } from "../store/engineStore";
import { useNodeLabelStore } from "./nodeLabelStore";

function roleLabel(node: SpatialNode) {
  if (!node.parentId) return "Starting world";
  const role = node.relation || node.cluster;
  const translations: Record<string, string> = {
    inner: "Direct drivers",
    middle: "Impacts & adoption",
    outer: "Alternatives & counter-signals",
  };
  return translations[role] || role || "Connected world";
}

function SelectedLabel({ node, nodes }: { node: SpatialNode; nodes: SpatialNode[] }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const select = useEngineStore((s) => s.select);
  const expand = useEngineStore((s) => s.expand);
  const generating = useEngineStore((s) => s.isGenerating);
  const parent = nodes.find((n) => n.id === node.parentId);
  const children = nodes.filter((n) => n.parentId === node.id);

  const readWorld = (id: string) => {
    useNodeLabelStore.getState().hover(null);
    select(id);
    // Keep keyboard / screen-reader focus with the newly opened label.
    requestAnimationFrame(() => document.getElementById("node-readout-toggle")?.focus({ preventScroll: true }));
  };

  const explore = async () => {
    if (generating) return;
    setError("");
    try {
      if (!(await expand(node.id))) setError("No new branches returned. Your existing worlds are still here.");
    } catch {
      setError("This world could not expand. Your existing worlds are still here.");
    }
  };

  return (
    <section className="node-readout" aria-label="World label">
      <button
        id="node-readout-toggle"
        className="node-readout__toggle"
        type="button"
        aria-expanded={open}
        aria-controls="node-readout-content"
        onClick={() => setOpen((value) => !value)}
      >
        <span>{open ? "Selected world" : "Show world label"}</span>
        <span aria-hidden="true">{open ? "−" : "+"}</span>
      </button>
      <div id="node-readout-content" hidden={!open}>
        <div className="node-readout__content">
          <div aria-live="polite" aria-atomic="true">
            <p className="node-readout__meta">{roleLabel(node)} · Depth {node.generationDepth}</p>
            <h2 id="node-readout-title" tabIndex={-1}>{node.name}</h2>
          </div>
          {node.why && (
            <details className="node-readout__details">
              <summary>Read this forecast</summary>
              <p>{node.why}</p>
            </details>
          )}
          {parent && (
            <button className="node-readout__parent" type="button" onClick={() => readWorld(parent.id)}>
              <span aria-hidden="true">↖</span>
              <span><small>Parent world</small>{parent.name}</span>
            </button>
          )}
          {children.length > 0 ? (
            <details className="node-readout__details">
              <summary>Read connected worlds · {children.length}</summary>
              <ul className="node-readout__branches">
                {children.map((child, index) => (
                  <li key={child.id}>
                    <button type="button" onClick={() => readWorld(child.id)}>
                      <span className="node-readout__number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                      <span><small>{roleLabel(child)}</small>{child.name}</span>
                      <span aria-hidden="true">↗</span>
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          ) : (
            <button className="node-readout__expand" type="button" disabled={generating} onClick={() => void explore()}>
              {generating ? "Mapping branches…" : "Expand this world ↗"}
            </button>
          )}
          {error && <p className="node-readout__error" role="status">{error}</p>}
          <p className="node-readout__hint">Tap a world to explore. Drag to look around. Pinch to zoom.</p>
        </div>
      </div>
    </section>
  );
}

export function NodeLabels() {
  const nodes = useEngineStore((s) => s.nodes);
  const selectedId = useEngineStore((s) => s.selectedId);
  const selected = nodes.find((node) => node.id === selectedId);

  return (
    <>
      {selected && <SelectedLabel key={selected.id} node={selected} nodes={nodes} />}
    </>
  );
}
